<template>
  <Popup :header="$t('menu.myTasks')" :fullPage="true" :showCloseButton="true" :contentScrim="false" @close="$router.go(-1)">
    <div
      class="container-tasks v-tracklify-scroll w-full h-full overflow-auto max-sm:h-fit max-sm:pb-5"
    >
      <div
        class="tasks-inner relative mx-auto w-240 max-w-full px-4 pt-7 pb-14 max-sm:pb-0 flex items-start gap-12.5
               max-[110rem]:flex-col max-[110rem]:items-center max-[110rem]:justify-start"
      >
        <div class="wrapper-left min-w-0 flex-1 max-[110rem]:order-2 max-[110rem]:w-full">
          <div class="content__title flex items-end justify-between gap-4 mb-7">
            <div>
              <h1 class="tasks-category mt-0 mb-1.5 text-2xl font-bold tracking-[-0.02em] text-text">
                {{ $t('myTasks.task_sorted_by_priority') }}
              </h1>
              <SkeletonLoader v-if="myTasksLoading" type="text" class="mt-1 h-3.5! w-64!" />
              <div v-else class="tasks-count-row flex items-center flex-wrap gap-4 text-body text-muted">
                <span>{{ $t('myTasks.total') }} {{ totalTasks }}</span>
                <span>
                  <span v-if="totalUpdates === 0">{{ $t('myTasks.no') }}</span>
                  {{ $t('myTasks.total_updates') }}
                  <span v-if="totalUpdates > 0" class="text-accent font-bold">{{ totalUpdates }}</span>
                </span>
                <span>
                  <span v-if="mentionedTasks.length === 0">{{ $t('myTasks.no') }}</span>
                  {{ $t('myTasks.total_unreads') }}
                  <span v-if="mentionedTasks.length > 0" class="text-accent font-bold">
                    {{ mentionedTasks.length }}
                  </span>
                </span>
              </div>
            </div>
            <SimpleButton
              size="md"
              class="ml-auto gap-1.5!"
              @click="$router.push({ name: 'workspaceSettings', query: { tab: 'Priorities' } })"
            >
              <IconListNumbers class="size-3.5" />
              {{ $t('projectPriorities.title') }}
            </SimpleButton>
            <div class="column-switcher flex gap-1 p-0.75 rounded-xl bg-input light:bg-input-solid border border-line max-lg:hidden">
              <button
                v-for="count in [2, 3]"
                :key="count"
                type="button"
                class="column-btn p-0 w-8 h-7 rounded-lg border-0 flex items-center justify-center cursor-pointer
                       transition-[background-color,color] duration-150"
                :class="columnsCount === count
                  ? 'bg-accent text-on-accent'
                  : 'bg-transparent text-text-muted hover:bg-hover hover:text-accent'"
                :title="`${count} columns`"
                @click="setColumnsCount(count)"
              >
                <IconColumns2Filled v-if="count === 2" class="size-3.5" />
                <IconColumns3Filled v-else class="size-3.5" />
              </button>
            </div>
          </div>
          <div
            class="content__priority flex flex-col"
            :class="columnsCount === 3 && !mobileMode ? '[--task-w:294px]' : '[--task-w:auto]'"
          >
            <div
              class="content__priority-group mb-8"
              v-for="taskGroup in tasksGroups"
              :key="`task_column-grp-${taskGroup.groupId}`"
            >
              <div class="group-name-row flex items-center gap-2 mb-3.5">
                <SkeletonLoader v-if="myTasksLoading" type="text" class="h-4! w-40!" />
                <h2
                  v-else
                  class="group-name my-0 text-group font-bold tracking-[-0.01em] truncate max-w-[250px]"
                  :class="taskGroup.groupId?.startsWith('project-') ? 'text-text' : 'text-accent'"
                >
                  {{ taskGroup.groupName }}
                </h2>
                <router-link
                  v-if="taskGroup.groupId?.startsWith('project-')"
                  :to="projectRoute(taskGroup)"
                  :title="$t('myTasks.openProject')"
                  class="open-project-btn shrink-0 flex items-center text-text-muted hover:text-accent transition-colors duration-150"
                  @click="emit('close')"
                >
                  <i class="tr tr-open-window"></i>
                </router-link>
              </div>
              <div class="group-tasks flex flex-col" v-if="taskGroup.tasks.length">
                <masonry-wall :items="taskGroup.tasks"
                  :columns="mobileMode ? 1 : columnsCount"
                >
                  <template #default="{ item }">
                    <div
                      v-if="item"
                      :key="`task_mv_${item.id}`"
                    >
                      <Task
                        v-if="item.projectId"
                        class="task"
                        :ref="item.id"
                        :task="item"
                        :assigneeSelectList="() => [getUser(item.assigneeId)]"
                        :simplified="false"
                        :showStageAndProject="true"
                        @click="openTaskDialog(item)"
                      />
                      <Task
                        class="task skeleton-task"
                        v-else
                        :simplified="true"
                        :skeleton="item"
                      />
                    </div>
                  </template>
                </masonry-wall>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="mentionedTasks.length"
          class="wrapper-right flex w-75 shrink-0 flex-col gap-12.5 max-[110rem]:order-1 max-[110rem]:w-full
                 min-[110rem]:absolute min-[110rem]:top-7 min-[110rem]:left-full min-[110rem]:ml-8.5"
        >
          <div class="wrapper-mentions" v-if='mentionedTasks.length'>
            <div class="content__title flex items-end justify-between gap-4.5 mb-8">
              <div>
                <h2 class="tasks-category max-sm:m-0 text-title font-bold tracking-[-0.02em] text-text">{{ $t('myTasks.mentions') }}</h2>
                <div class="tasks-count mt-1.5 text-label text-text-muted">{{ $t('myTasks.total') }} {{ mentionedTasks.length }}</div>
              </div>
              <ConfirmButton
                :disabled="isMarkingAllAsRead"
                :popupTitle="$t('myTasks.confirmMarkAllAsRead')"
                :popupSubTitle="$t('myTasks.confirmMarkAllAsReadMessage')"
                :buttonText="''"
                :buttonIcon="''"
                @confirm="markAllMentionsAsRead"
              >
                <template #menu-trigger>
                  <button
                    type="button"
                    :disabled="isMarkingAllAsRead"
                    :title="$t('myTasks.confirmMarkAllAsRead')"
                    class="column-btn p-0 w-8 h-7 rounded-lg border border-line bg-input text-text-muted flex items-center
                           justify-center cursor-pointer transition-[background-color,color,border-color]
                           duration-150 enabled:hover:bg-hover enabled:hover:border-accent
                           enabled:hover:text-accent disabled:opacity-45 disabled:cursor-not-allowed"
                  >
                    <IconChecks class="size-3.5" />
                  </button>
                </template>
              </ConfirmButton>
            </div>
            <div class="wrapper-tasks flex flex-col gap-4.5 max-sm:[--task-w:auto]">
              <div v-for="task in mentions" :key="`task_${task.id}`">
                <Task
                  v-if="task.projectId"
                  class="task"
                  :ref="task.id"
                  :task="task"
                  :assigneeSelectList="() => [getUser(task.assigneeId)]"
                  :simplified="false"
                  :showStageAndProject="true"
                  @click="openTaskDialog(task)"
                />
                <Task
                  v-else
                  class="task skeleton-task"
                  :simplified="true"
                  :skeleton="task"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Popup>
</template>
<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Task from '@/components/widgets/Task';
import SkeletonLoader from '@/components/elements/SkeletonLoader';
import Popup from '@/components/elements/Popup';
import MasonryWall from '@/components/elements/MasonryWall';
import ConfirmButton from '@/components/elements/ConfirmButton';
import SimpleButton from '@/components/elements/SimpleButton';
import { IconColumns2Filled, IconColumns3Filled, IconChecks, IconListNumbers } from '@iconify-prerendered/vue-tabler';

const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const mobileMode = computed(() => store.state.system.mobileMode);
const columnsCount = ref(parseInt(localStorage.getItem('myTasksColumnsCount'), 10) || 2);
const isMarkingAllAsRead = ref(false);

const SKELETON_LOADERS_MOCK = {
  tasksGroups: [
    {
      groupId: 'skeleton-1',
      tasks: [
        { id: '555', projectStage: true, image: { h: '145' } },
        {
          id: '111', projectStage: true, image: { h: '170' }, tags: true, rows: ['fullwidth', 'fullwidth', 'halfwidth'],
        },
        {
          id: '222', projectStage: true, image: { h: '82' }, rows: ['fullwidth', 'halfwidth'],
        },
      ],
    },
    {
      groupId: 'skeleton-2',
      tasks: [
          {
          id: '666', projectStage: true, image: { h: '92.39' }, tags: true, rows: ['fullwidth'],
        },
        {
          id: '444', projectStage: true, tags: true, rows: ['threequarters'],
        },
        {
          id: '333', projectStage: true, tags: true, rows: ['fullwidth', 'fullwidth', 'halfwidth'],
        },
      ],
    },

  ],
};

const getUser = computed(() => store.getters['user/getUser']);


const myTasksLoading = ref(false);
const prioTasksHeights = ref([[], []]);
const mentionsTasksHeights = ref([]);
const likedTasksHeights = ref([]);

    
const mentionedTasks = computed(() => store.state.user.mentionedTasks || []);
const likedTasks = computed(() => store.state.user.likedTasks || []);


const tasksGroups = computed(() => {
  if (myTasksLoading.value || !store.state.user.myTasksByGroup) {
    return SKELETON_LOADERS_MOCK.tasksGroups;
  }
  const likedIds = new Set(likedTasks.value.map(task => task.id));
  return store.state.user.myTasksByGroup
    .flatMap((group) => {
      if (group.groupId.startsWith('project-')) {
        return { ...group, tasks: group.tasks.filter(task => !likedIds.has(task.id)) };
      }
      if (group.groupId !== 'urgency') return group;
      const urgentIds = new Set(group.tasks.map(task => task.id));
      return [group, {
        groupId: 'liked',
        groupName: t('myTasks.liked'),
        tasks: likedTasks.value.filter(task => !urgentIds.has(task.id)),
      }];
    })
    .filter(group => group.tasks?.length > 0);
});

const mentions = computed(() => {
  const skeletons = localStorage.getItem('mentionedTasks');
  return (myTasksLoading.value && skeletons && JSON.parse(skeletons)) || store.state.user.mentionedTasks;
});

const openTaskDialog = (task) => {
  emit('close');
  store.commit('project/mutationOpenTaskPopup', { task });
};

const projectRoute = (taskGroup) => {
  const { project } = taskGroup.tasks[0];
  return {
    name: 'project',
    params: {
      projectSlug: project.slug,
      slugNameProject: project.nameSlug || project.slug,
    },
  };
};

const setColumnsCount = (count) => {
  columnsCount.value = count;
  localStorage.setItem('myTasksColumnsCount', count.toString());
};

const markAllMentionsAsRead = async () => {
  if (isMarkingAllAsRead.value) return;
  
  try {
    isMarkingAllAsRead.value = true;
    
    // Mark all mentions as read by calling the API for each mentioned task
    const promises = mentionedTasks.value.map(task => {
      console.log('Marking task as read:', task, store.state.user)
      return store.dispatch('project/actionMarkAsRead', {
        taskId: task.id,
        option: 'all',
        ttbuId: task.taskTrackedByUsers.find(ttbu => ttbu.userId === store.state.user.user.id)?.id,
      });
    });
    
    await Promise.all(promises);
    
    // Refresh the mentions list
    await store.dispatch('user/actionGetLikesAndMentions');
    
  } catch (error) {
    console.error('Failed to mark all mentions as read:', error);
  } finally {
    isMarkingAllAsRead.value = false;
  }
};

const totalTasks = computed(() => {
  if (myTasksLoading.value) return 0;
  return tasksGroups.value.reduce((acc, group) => acc + group.tasks.length, 0);
});

const totalUpdates = computed(() => {
  if (myTasksLoading.value) return 0;

  return tasksGroups.value.reduce((acc, group) => {
    const updatesInGroup = group.tasks.filter(task => {
      const ttbu = task.taskTrackedByUsers?.find(x => x.userId === store.state.user.user.id);
      if (!ttbu) return false;
      return Date.parse(task.lastEventTs) > Date.parse(ttbu.tsOfLastEventIveSeen);
    }).length;

    return acc + updatesInGroup;
  }, 0);
});


onMounted(async () => {
  myTasksLoading.value = true;
  let stopWaitingTimeout = setTimeout(() => {
    myTasksLoading.value = false;
  }, 4 * 1000);
  await store.dispatch('user/actionGetAllMyTasks');
  clearTimeout(stopWaitingTimeout);
  stopWaitingTimeout = null;
  myTasksLoading.value = false;
  await store.dispatch('user/actionGetLikesAndMentions');
});

watch(prioTasksHeights, (values) => {
  if (
    prioTasksHeights.value?.length &&
    store.state.user.tasksColumns[0].length === values[0].length &&
    store.state.user.tasksColumns[1].length === values[1].length
  ) {
    localStorage.setItem('tasksColumns', JSON.stringify(values));
  }
});

watch(mentionsTasksHeights, (values) => {
  if (mentionsTasksHeights.value?.length === values.length) {
    localStorage.setItem('mentionedTasks', JSON.stringify(values));
  }
});

watch(likedTasksHeights, (values) => {
  if (likedTasksHeights.value?.length === values.length) {
    localStorage.setItem('likedTasks', JSON.stringify(values));
  }
});
</script>
